import dayjs from 'dayjs';
import React, { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Box,
  Grid,
  Tooltip,
  Checkbox,
  IconButton,
  CardContent,
  FormControlLabel,
  Typography,
  Divider,
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardHeader,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { Add, Close, Edit, Home, Info, WarningRounded } from '@material-ui/icons';

import theme from '../../../../theme';
import KPTable from '../../../../components/KPTComponents/KPTable';
import Loading from '../../../../components/CustomComponents/Loading';
import KPTextform from '../../../../components/KPTComponents/KPTextform';
import InputActives from '../../../GTNS/ActivesField/InputActives';
import PreviewModal from '../../../../components/CustomComponents/PreviewModal';
import exportProfile from '../../../../utils/kvc.exportProfile';
import ThanhTichAlert from '../../../AppLayout/ThanhTichAlert/ThanhTichAlert';
import ThanhTichInsert from '../../ThanhTichInsert/ThanhTichInsert';

import { columnRender } from '../../../../utils/nstb/columnRender';
import { useScreenSize } from '../../../../hooks/useScreenSize';
import { jumpToReleventDiv } from '../../../../components/KPTComponents/utils';
import { fileRender, photoRender } from '../../../../utils/photoRender';
import { capitalizeFirstLetter } from '../../../../utils';
import { fileActions, userActions, formActions } from '../../../../actions';
import { kvcAchievementFields, kvcActivityFields, kvcPersonalProcessField } from '../../../../utils';
import { ACHIEVEMENT_TYPE, CATEGORY, KEYS, SECONDARY_COLOR } from '../../../../constants/enums';
import { FORM_CONSTANTS, MONOGRAPHS_CONSTANTS, REPORTS_CONSTANTS, ROUTER } from '../../../../constants';
import {
  KVC2026_LINKS,
  KVC2026_TYPICAL_WORK_FIELDS,
  composeTypicalWorkHtml,
} from '../../../../constants/kvc2026.config';

const ACTIONS = ['link', 'edit', 'delete'];

const safeParseJSON = (str, fallback) => {
  try {
    const parsed = JSON.parse(str);
    return parsed ?? fallback;
  } catch (e) {
    return fallback;
  }
};

export default function DangKyHoSoUpdate() {
  const dispatch = useDispatch();
  const isMobileMode = useScreenSize();

  const [files, setFiles] = useState({});
  const [dialog, setDialog] = useState(null);
  const [values, setValues] = useState(null);
  const [showCSKH, setShowCSKH] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [acceptRules, setAcceptRules] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const [commitment, setCommitment] = useState({});
  const [socialActivity, setSocialActivity] = useState([]);
  const [personalProcess, setPersonalProcess] = useState([]);
  const [stateReward, setStateReward] = useState([]);
  const [ministryReward, setMinistryReward] = useState([]);
  const [personalAchievement, setPersonalAchievement] = useState([]);

  const profile = useSelector((state) => state.profileReducer.profile);
  const seasonList = useSelector(state => state.seasonReducer.seasonList);
  const application = useSelector((state) => state.formReducer.applicationList?.[0] || null);
  const openSeasonId = useSelector((state) => state.seasonReducer.openSeasonId);
  // Bảng điểm (mục 1.1-2.4): dùng để tra "Phân loại" khi xuất file .docx theo
  // code mới (qcv_score_infos).
  const scoreInformationList = useSelector((state) => state.scoreInformationReducer.scoreInformationList);

  const loading = useSelector((state) => (
    state.userReducer.loading
    || state.formReducer.loading
    || state.profileReducer.loading
    || uploadingFiles
  ));

  // ---------------- ARCHIVEMENTS ---------------- //
  const achievementData = useSelector((state) => ({
    [ACHIEVEMENT_TYPE.MONOGRAPH]: state.monographReducer.personalMonographList,
    [ACHIEVEMENT_TYPE.RESEARCH_PROJECT]: state.researchProjectReducer.personalResearchProjectList,
    [ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT]: state.reportsReducer.personalReportList,
    [ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH]: state.researchReducer.personalResearchList,
    [ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.DOMESTIC]: state.researchReducer.personalResearchList.filter((e) => e.filterType === KEYS.DOMESTIC),
    [ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.INTERNATIONAL]: state.researchReducer.personalResearchList.filter((e) => e.filterType === KEYS.INTERNATIONAL),
    // LƯU Ý: backend bảng "report" (/api/v1/report) không có cột filterType nên
    // report tạo xong quay về KHÔNG kèm filterType. Vì vậy không thể chỉ dựa vào
    // filterType. Report KVC chỉ có 2 loại: 1.2 quốc tế (type ∈ INTERNATIONAL) và
    // 2.2 trong nước -> "không phải quốc tế" ⇒ trong nước.
    [ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT + KEYS.DOMESTIC]: state.reportsReducer.personalReportList.filter(
      (e) => e.filterType === KEYS.DOMESTIC
        || (!e.filterType && !REPORTS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((option) => e.type === option.value))
    ),
    [ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT + KEYS.INTERNATIONAL]: state.reportsReducer.personalReportList.filter(
      (e) => e.filterType === KEYS.INTERNATIONAL
        || (!e.filterType && REPORTS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((option) => e.type === option.value))
    ),
    [ACHIEVEMENT_TYPE.MONOGRAPH + KEYS.INTERNATIONAL]: state.monographReducer.personalMonographList.filter(
      (e) => MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL.find((option) => e.type === option.value)
    ),
    [ACHIEVEMENT_TYPE.MONOGRAPH + KEYS.DOMESTIC]: state.monographReducer.personalMonographList.filter(
      (e) => MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.DOMESTIC.find((option) => e.type === option.value)
    ),
  }));

  const handleFileChange = (e, name) => setFiles({ ...files, [name]: e });

  const textForm = {
    base: [
      { key: 'name', width: isMobileMode ? 12 : 8, label: 'Họ và tên', value: profile?.name, required: true, disabled: true },
      {
        key: 'birth',
        width: isMobileMode ? 12 : 4,
        label: 'Ngày sinh',
        value: dayjs(profile?.birth).format('DD/MM/YYYY'),
        type: 'date',
        disabled: true,
        required: true,
      },
      { key: "gender", width: isMobileMode ? 6 : 3, label: "Giới tính", value: profile?.gender, options: FORM_CONSTANTS.GENDER_OPTIONS, disabled: true },
      { key: "ethnic", width: isMobileMode ? 6 : 3, label: "Dân tộc", value: profile?.ethnic, disabled: true },
      { key: 'phone', width: isMobileMode ? 12 : 6, label: 'Số điện thoại', value: profile?.phone, required: true, disabled: true },
      { key: 'email', width: 12, label: 'Email', value: profile?.email, required: true, disabled: true },
      { key: 'website', width: 12, height: 1, label: 'Website/Facebook', required: true, value: profile?.website, disabled: true },
    ],
    partI: [
      { key: "unit", width: isMobileMode ? 12 : 7, label: "Đơn vị công tác", required: true },
      {
        key: "field",
        width: isMobileMode ? 12 : 5,
        label: "Lĩnh vực",
        required: true,
        options: FORM_CONSTANTS.FIELD_OPTIONS[CATEGORY.KVC].map((opt, i) => ({
          ...opt,
          title: `${i + 1}. ${opt.title}`,
        })),
      },
      { key: "nominator", width: 12, label: "Đơn vị đề cử", required: true },
      {
        key: "facultyPhone",
        width: 12,
        label: 'Link file công văn cơ quan, đơn vị đề cử',
        labelRender: (
          <Typography className="kpt-textfield-label" style={{ display: "inline" }}>
            {`Link file công văn cơ quan, đơn vị đề cử (Tải file mẫu `}
            <a
              href={KVC2026_LINKS.mauCongVanDeCu}
              style={{ color: SECONDARY_COLOR }}
              target="_blank"
              rel="noreferrer"
            >
              tại đây
            </a>
            {")"}
          </Typography>
        ),
        required: true,
      },
    ],
    partII: [
      {
        key: "academicLevel",
        width: 6,
        label: "Trình độ học vấn",
        required: true,
        value: values?.academicLevel,
        options: FORM_CONSTANTS.ACADEMIC_LEVEL_OPTIONS,
      },
      {
        key: "academicRank",
        width: 6,
        label: isMobileMode ? "Học hàm/chức danh" : "Học hàm/chức danh khoa học",
        value: values?.academicRank,
      },
      {
        key: "languague",
        width: 6,
        label: "Ngoại ngữ",
        required: true,
        value: values?.languague,
      },
      {
        key: "politicalUnion",
        width: 6,
        label: "Đoàn thể chính trị",
        required: true,
        value: values?.politicalUnion,
        options: FORM_CONSTANTS.POLITICAL_UNION_OPTIONS,
      },
      {
        key: "gifted",
        width: 12,
        height: 2,
        label: "Năng khiếu",
        required: true,
        value: values?.gifted,
      },
      {
        key: "videoUrl",
        width: 12,
        label: "Link video giới thiệu bản thân và hình ảnh hoạt động cá nhân",
        required: true,
        value: values?.videoUrl,
        labelRender: (
          <Typography className="kpt-textfield-label">
            Link video giới thiệu bản thân và hình ảnh hoạt động cá nhân
            <span style={{ color: theme.palette.secondary.main }}>*</span>:
            <i style={{ display: "block", fontSize: 13, fontWeight: 400, color: theme.palette.secondary.main }}>
              Lưu ý:
              <span style={{ display: "block" }}>
                + Video giới thiệu cá nhân không quá 02 phút, giới thiệu về cá
                nhân, các thành tích, công trình NCKH hoặc giải pháp công nghệ
                tiêu biểu, giá trị khoa học, khả năng ứng dụng và tác động xã
                hội.
              </span>
              <span style={{ display: "block" }}>
                + 05 ảnh về hoạt động cá nhân trong học tập, nghiên cứu và công
                tác.
              </span>
              <span style={{ display: "block" }}>
                + Video, hình ảnh phải bảo đảm chất lượng, âm thanh rõ, hình thức
                đẹp, chuyên nghiệp, dung lượng và định dạng phù hợp để Ban Tổ chức
                sử dụng, thiết kế sản phẩm truyền thông.
              </span>
              <span style={{ display: "block" }}>
                + Vui lòng kiểm tra và thiết lập quyền truy cập link Google Drive
                trước khi gửi để Ban tổ chức có thể truy cập.
              </span>
            </i>
          </Typography>
        ),
      },
    ],
    typicalWork: [
      {
        key: "typical-work-note",
        width: 12,
        value: true,
        render: (
          <Typography align="justify" className="kpt-textfield-label">
            <i style={{ fontWeight: 400 }}>
              Ứng viên tự giới thiệu 01 công trình nghiên cứu khoa học tiêu biểu
              nhất của bản thân đã được công bố. Ứng viên phải là tác giả độc lập
              hoặc tác giả chính (chủ biên, tác giả thứ nhất hoặc tác giả liên
              hệ). Bản giới thiệu có độ dài từ 1500 đến 2000 từ.
            </i>
          </Typography>
        ),
      },
      ...KVC2026_TYPICAL_WORK_FIELDS.map((f, i) => ({
        key: f.key,
        width: 12,
        height: 4,
        value: values?.[f.key],
        required: true,
        label: `Công trình tiêu biểu - ${f.label}`,
        labelRender: (
          <Typography className="kpt-textfield-label">
            {`${i + 1}) ${f.label}`}
            <span style={{ color: theme.palette.secondary.main }}>*</span>:
            <i style={{ display: "block", fontSize: 13, fontWeight: 400 }}>
              {f.hint}
            </i>
          </Typography>
        ),
      })),
      {
        key: 'sumaryFile',
        width: 12,
        label: 'Bản công trình tiêu biểu giới thiệu có xác nhận của cơ quan có thẩm quyền',
        labelRender: (
          <>
            <Typography variant='body1' style={{ fontSize: 17 }}>
              <b>Bản công trình tiêu biểu giới thiệu có xác nhận của cơ quan có thẩm quyền (bản scan, chữ ký, đóng dấu)</b>
            </Typography>
          </>
        ),
        type: 'file',
        required: true,
      },
    ],
    commitment: [
      {
        key: "commitment-note",
        width: 12,
        value: true,
        render: (
          <Typography variant="subtitle1" style={{ color: SECONDARY_COLOR }}>
            <i>
              <b>Lưu ý: </b>Viết kế hoạch hành động và cam kết của cá nhân thực
              hiện tối thiểu 03 năm kể từ khi được trao Giải thưởng.
            </i>
          </Typography>
        ),
      },
    ],
    camKetFile: [
      {
        key: 'camKetFile',
        width: 12,
        type: 'file',
        label: 'Bản cam kết của ứng viên',
        value: values?.camKetFile,
        required: true,
        labelRender: (
          <>
            <Typography variant='body1' style={{ fontSize: 17 }}>
              <b>Bản cam kết của ứng viên (bản scan có chữ ký)</b>
            </Typography>
            {KVC2026_LINKS.mauCamKet ? (
              <Typography variant='body1'>
                (Tải mẫu&nbsp;
                <a
                  href={KVC2026_LINKS.mauCamKet}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  style={{ textDecoration: 'underline', color: SECONDARY_COLOR }}
                >
                  tại đây
                </a>
                )
              </Typography>
            ) : null}
          </>
        ),
      },
    ],
    confirmationFile: [
      {
        key: 'confirmationFile',
        width: 12,
        type: 'file',
        filesLimit: 1,
        value: values?.confirmationFile,
        required: true,
        labelRender: (
          <>
            <Typography variant='body1' style={{ fontSize: 17 }}>
              <b>Tải file xin xác nhận</b>
              <span style={{ color: SECONDARY_COLOR }}>*</span>
            </Typography>
            <Typography variant='body1'>
              Trước khi ấn nộp hồ sơ, trích xuất Bản đăng ký tham gia Giải thưởng Khuê Văn Các&nbsp;
              <a
                onClick={async (event) => {
                  event.stopPropagation();
                  setUploadingFiles(true);
                  try {
                    const saved = await handleSave('unsubmit');
                    if (saved) {
                      const apps = await dispatch(
                        formActions.filterApplicationForm({
                          category: CATEGORY.XHNV,
                          seasonId: openSeasonId,
                          profileId: profile.id,
                        })
                      );
                      exportProfile(apps?.[0] || application, scoreInformationList);
                    }
                  } finally {
                    setUploadingFiles(false);
                  }
                }}
                style={{ textDecoration: 'underline', color: SECONDARY_COLOR }}
              >
                tại đây
              </a>
              &nbsp;và xin xác nhận, đóng dấu của thủ trưởng cơ quan, đơn vị quản lý trực tiếp rồi tải file đã xin xác nhận lên trước khi nộp.
            </Typography>
          </>
        ),
      },
    ],
  }

  const topPart = isMobileMode ? textForm.base.slice(0, 4) : textForm.base;
  const botPart = isMobileMode ? textForm.base.slice(4, textForm.base.length) : [];

  const achievementFields = kvcAchievementFields({ achievementData, actions: ACTIONS });
  const activityFields = kvcActivityFields({
    isMobileMode,
    stateReward,
    setStateReward,
    ministryReward,
    setMinistryReward,
    personalAchievement,
    setPersonalAchievement,
    socialActivity,
    setSocialActivity,
  });
  const processField = kvcPersonalProcessField({ isMobileMode, personalProcess, setPersonalProcess });

  const handleChangeActivityForm = (name, event, type, values, setValues) => {
    const keyList = name.split(".");
    const key = keyList[2];
    const index = parseInt(keyList[1]);

    const tmp = [...values];
    if(type === "date"){
      tmp.splice(index, 1, { ...tmp[index], [key]: event});
    } else if (type === "removeFile") {
      tmp.splice(index, 1, { ...tmp[index], [key]: undefined });
    } else {
      tmp.splice(index, 1, { ...tmp[index], [key]: event.target.value });
    }
    setValues(tmp);
  };

  const handleReload = (profileId) => {
    dispatch(formActions.getApplicationFormv2(profileId));
    dispatch(formActions.filterApplicationForm({
      category: CATEGORY.XHNV,
      seasonId: openSeasonId,
      profileId: profile.id,
    })).then((applications) => {
      if (applications.length > 0) {
        const application = applications[0];
        if (application.process === "") application.process = "[]";
        if (application.previewFile === "") application.previewFile = "[]";
        if (application.socialActivity === "") application.socialActivity = "[]";
        if (application.personalAchievement === "") application.personalAchievement = "[]";
        if (application.communicationActivity === "") application.communicationActivity = "[]";

        const fileList = safeParseJSON(application?.previewFile, []);
        const fileArr = Array.isArray(fileList) ? fileList : [];
        application.sumaryFile = fileArr.filter((file) => typeof file === 'string' && file.includes('sumaryFile')).map((file) => fileRender(file));
        application.camKetFile = fileArr.filter((file) => typeof file === 'string' && file.includes('camKetFile')).map((file) => fileRender(file));
        // File xin xác nhận: url đã lưu -> mảng để DropzoneArea hiển thị lại
        application.confirmationFile = application.confirmationFile
          ? [application.confirmationFile]
          : undefined;

        // A. Công trình tiêu biểu: nạp 4 mục từ typicalWorkContent; hồ sơ cũ
        // (summary là mảng/HTML) thì đưa nội dung cũ vào mục 1.
        const twContent = safeParseJSON(application.typicalWorkContent, null);
        if (twContent) {
          application.work1 = twContent.work1 || "";
          application.work2 = twContent.work2 || "";
          application.work3 = twContent.work3 || "";
          application.work4 = twContent.work4 || "";
        } else if (application.summary && application.summary.trim()) {
          const legacy = safeParseJSON(application.summary, null);
          if (Array.isArray(legacy)) {
            application.work1 = legacy.join("\n").replace(/<[^>]*>/g, " ").trim();
          } else {
            application.work1 = application.summary.replace(/<[^>]*>/g, " ").trim();
          }
        }

        // C.1 Khen thưởng: tách cấp Nhà nước (mặc định) và Bộ/ban/ngành/tỉnh.
        const comm = safeParseJSON(application.communicationActivity, []);
        const commArr = Array.isArray(comm) ? comm : [];

        setValues({ ...values, ...application });
        setCommitment(safeParseJSON(application.commitment, {}));
        setSocialActivity(safeParseJSON(application.socialActivity, []));
        setPersonalProcess(safeParseJSON(application.process, []));
        setPersonalAchievement(safeParseJSON(application.personalAchievement, []));
        setStateReward(commArr.filter((r) => (r?.rewardGroup || 'state') === 'state'));
        setMinistryReward(commArr.filter((r) => r?.rewardGroup === 'ministry'));

        if (application?.status === "in_review") setWarningOpen(true);
      } else {
        setValues({ ...values });
      }
    });
  }

  const upsertApplication = (status, uploadedFiles=[]) => {
    const getFile = (idKey, index) =>
      uploadedFiles.find((file) => {
        const part = file.split(`/storage/awardRegister/profile/${profile?.id}/`)[1];
        if (!part) return false;
        const keys = part.split('.');
        return keys[0] === idKey && parseInt(keys[1]) === index;
      });

    const buildActivity = (idKey, dataSource) =>
      (dataSource || [])
        .filter((data) => Object.values(data).length !== 0)
        .map((data, index) => ({
          ...data,
          [`${idKey}PreviewFile`]:
            getFile(idKey, index) || data?.[`${idKey}PreviewFile`] || "",
        }));

    const achivementFormData = {};
    [
      ACHIEVEMENT_TYPE.MONOGRAPH,
      ACHIEVEMENT_TYPE.RESEARCH_PROJECT,
      ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT,
      ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH,
    ].forEach((key) => {
      achivementFormData['form' + capitalizeFirstLetter(key)] = achievementData[key].map((e) => ({ [key + 'Id']: e.id }))
    });

    // Khen thưởng: gộp 1.1 (cấp Nhà nước) + 1.2 (Bộ/ban/ngành/tỉnh) vào 1 mảng.
    const stateRows = buildActivity('otherAchievement', stateReward).map((r) => ({ ...r, rewardGroup: 'state' }));
    const ministryRows = buildActivity('ministryReward', ministryReward).map((r) => ({ ...r, rewardGroup: 'ministry' }));
    const _process = (personalProcess || []).filter((d) => Object.values(d).length !== 0);

    // File minh chứng dùng chung cột previewFile (sumaryFile + camKetFile).
    const oldList = (() => {
      const a = safeParseJSON(application?.previewFile, []);
      return Array.isArray(a) ? a : [];
    })();
    const newSumary = uploadedFiles.find((f) => f.includes('sumaryFile'));
    const newCamKet = uploadedFiles.find((f) => f.includes('camKetFile'));
    const sumaryUrl =
      newSumary ||
      oldList.find((f) => typeof f === 'string' && f.includes('sumaryFile')) ||
      (typeof files?.sumaryFile === 'string' ? files.sumaryFile : '') ||
      '';
    const camKetUrl =
      newCamKet ||
      oldList.find((f) => typeof f === 'string' && f.includes('camKetFile')) ||
      (typeof files?.camKetFile === 'string' ? files.camKetFile : '') ||
      '';
    const previewFile = [sumaryUrl, camKetUrl].filter(Boolean);

    // File xin xác nhận: url mới upload, hoặc giữ url cũ nếu không đổi.
    const confirmationFile =
      uploadedFiles.find((f) => f.includes('confirmationFile')) ||
      (typeof values?.confirmationFile?.[0] === 'string'
        ? values.confirmationFile[0]
        : application?.confirmationFile) ||
      '';

    const typicalWorkData = {
      work1: values?.work1 || "",
      work2: values?.work2 || "",
      work3: values?.work3 || "",
      work4: values?.work4 || "",
    };

    const payload = {
      ...values,
      ...achivementFormData,
      status,
      seasonId: openSeasonId,
      category: CATEGORY.XHNV,
      profileId: profile.id,
      previewFile: JSON.stringify(previewFile),
      confirmationFile,

      typicalWorkContent: JSON.stringify(typicalWorkData),
      summary: composeTypicalWorkHtml(typicalWorkData),
      commitment: JSON.stringify(commitment || {}),

      process: JSON.stringify(_process),
      communicationActivity: JSON.stringify([...stateRows, ...ministryRows]),
      [KEYS.SOCIAL_ACTIVITY]: JSON.stringify(buildActivity('socialActivity', socialActivity)),
      [KEYS.PERSONAL_ACHIEVEMENT]: JSON.stringify(buildActivity('personalAchievement', personalAchievement)),
    }

    // Hồ sơ rỗng thì tạo hồ sơ mới, không thì cập nhật đè lên hồ sơ cũ
    if (!application) {
      dispatch(formActions.insertApplicationForm(payload)).then((success) => {
        if (success && status === 'in_review') window.location.reload();
      });
    } else {
      dispatch(formActions.updateApplicationForm(payload)).then((success) => {
        if (success && status === 'in_review') window.location.reload();
      });
    }
  }

  const handleSave = async (status) => {
    const formFields = [
      ...textForm.partI,
      ...textForm.partII,
      ...textForm.typicalWork,
      ...textForm.camKetFile,
      ...textForm.confirmationFile,
    ];

    for (const elm of formFields) {
      if (status === "unsubmit" && (elm.key === "sumaryFile" || elm.key === "camKetFile" || elm.key === "confirmationFile")) continue;
      if (elm.value === true) continue; // các block render thuần (note...)

      const value = (elm.value !== undefined ? elm.value : undefined) ?? values?.[elm.key];

      if (!elm.disabled && elm.required && !value) {
        setValues({ ...values, emptyKey: elm.key });
        jumpToReleventDiv(elm.key);
        dispatch(
          userActions.notification({
            message: `Không được để trống thông tin về: ${elm?.label}`,
            type: 'error',
          }),
        );
        return;
      }
    }

    if (!profile?.id) {
      dispatch(userActions.notification({ message: "Lỗi profile ID, vui lòng tải lại trang!", type: 'error' }));
      return false;
    }

    const uploadFiles = {...files};
    const confInput = values?.confirmationFile?.[0];
    if (confInput instanceof File) uploadFiles.confirmationFile = confInput;

    if (Object.keys(uploadFiles).length > 0) {
      setUploadingFiles(true);
      dispatch(fileActions.importMultiFileQCV(profile.id, uploadFiles)).then((uploadedFiles) => {
        setUploadingFiles(false);
        if (!uploadedFiles) {
          dispatch(userActions.notification({ message: "Lỗi upload files, vui lòng tải lại trang!", type: 'error' }));
          return;
        }
        upsertApplication(status, uploadedFiles)
      });
    } else {
      upsertApplication(status);
    }

    return true;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave('in_review');
  };

  const openSeason = useMemo(() => seasonList.find((s) => s.id === openSeasonId), [seasonList, openSeasonId]);

  const isNotAvalaible = useMemo(() => {
    if (!openSeason) return false;
    const { openDate, closeDate } = openSeason;
    const currDate = new Date();
    if (currDate < new Date(openDate)) return 'Chưa mở cổng đăng ký';
    if (currDate > new Date(closeDate)) return 'Đã hết thời hạn đăng ký';
    return null;
  }, [openSeason]);

  useEffect(() => {
    if (values?.sumaryFile?.[0]) {
      setFiles((prev) => ({ ...prev, sumaryFile: values?.sumaryFile?.[0] }));
    }
  }, [values?.sumaryFile]);

  useEffect(() => {
    if (values?.camKetFile?.[0]) {
      setFiles((prev) => ({ ...prev, camKetFile: values?.camKetFile?.[0] }));
    }
  }, [values?.camKetFile]);

  useEffect(() => {
    if (profile?.id) handleReload(profile?.id);
  }, [profile]);

  // Đầu mục lớn (THÔNG TIN CÁ NHÂN / THÀNH TÍCH CÁ NHÂN)
  const bigHeader = (title, marginTop = 96) => (
    <Card style={{ width: '100%', marginTop }}>
      <Box height={6} bgcolor='#233871' />
      <Box padding={2}>
        <Typography variant='h5' style={{ textAlign: 'center' }}>
          <b>{title}</b>
        </Typography>
      </Box>
    </Card>
  );

  // Đầu mục con A/B/C/D
  const sectionHeader = (title) => (
    <Card style={{ width: '100%', marginTop: 32 }}>
      <Box padding={2} bgcolor='#f5f1ee'>
        <Typography variant='h5'>
          <b>{title}</b>
        </Typography>
      </Box>
    </Card>
  );

  const renderActivityGroup = (active) => (
    <Card key={active.key} style={{ width: '100%', marginTop: 32 }}>
      {active?.children ? (
        <>
          <Box padding='8px 16px' display='flex' alignItems='center'>
            <Typography variant='h6' style={{ flexGrow: 1 }}>
              <b>{active.title}</b>
            </Typography>
          </Box>
          <Divider />
          {active.children.map((e) => (
            <Fragment key={e.key}>
              <InputActives
                id={e.key}
                {...e}
                newFileLength={0}
                setPreview={setPreviewFile}
                handleFileChange={handleFileChange}
                handleChangeForm={handleChangeActivityForm}
              />
              <Divider />
            </Fragment>
          ))}
        </>
      ) : (
        <InputActives
          id={active.key}
          {...active}
          newFileLength={0}
          setPreview={setPreviewFile}
          handleFileChange={handleFileChange}
          handleChangeForm={handleChangeActivityForm}
        />
      )}
    </Card>
  );

  return (
    <>
      <Card style={{ width: '100%', marginTop: 64 }}>
        <Box height={6} bgcolor='#233871' />
        <Box padding={2}>
          <Typography variant='h4' style={{ textAlign: 'center', textTransform: 'uppercase' }}>
            <b>Đăng ký hồ sơ</b>
          </Typography>
        </Box>
      </Card>

      <Card style={{ width: '100%', marginTop: 32, display: showCSKH ? 'block' : 'none' }}>
        <CardHeader style={{ paddingRight: 4, paddingTop: 2, paddingBottom: 2 }} title={
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant="h6">
              <b>Hỗ trợ đăng ký Giải thưởng Khuê Văn Các</b>
            </Typography>
            <IconButton onClick={() => setShowCSKH(false)}><Close /></IconButton>
          </Box>
          }
        />
        <Divider />
        <CardContent style={{ paddingTop: 2, paddingBottom: 2 }}>
          <ul>
            <li>Hỗ trợ đăng ký Giải thưởng Khuê Văn Các: Mr Hiếu (<a href='tel:0394776343'>039 477 6343</a>)</li>
            <li style={{ marginTop: 4 }}>Hỗ trợ kỹ thuật hệ thống: Ms Trang (<a href='http://zalo.me/0339028002'>0339028002</a>)</li>
          </ul>
        </CardContent>
      </Card>

      <form className='dkhs-container' id='form-update' defaultValue={values} onSubmit={handleSubmit}>
        {/* ============ THÔNG TIN CÁ NHÂN ============ */}
        {bigHeader('THÔNG TIN CÁ NHÂN', 64)}

        <Card style={{ width: '100%', marginTop: 32 }}>
          <CardContent>
            <Box display='flex'>
              <Box flexGrow={1} marginTop='4px'>
                <KPTextform component='div' textForm={topPart} />
              </Box>
              <Box marginTop='4px' marginLeft={1}>
                <Typography className='kpt-textfield-label'>
                  {isMobileMode ? 'Ảnh chân dung' : 'Ảnh chân dung (4x6)'}
                  <span style={{ color: theme.palette.secondary.main }}>*</span>
                  {':'}
                </Typography>
                <Box>
                  <Card className='dkhs-photoContainer'>
                    <img alt='anh-chan-dung' className='dkhs-photo' src={photoRender(profile?.photoUrl)} />
                  </Card>
                </Box>
              </Box>
            </Box>
            <Box flexGrow={1} marginTop='4px'>
              <KPTextform component='div' textForm={botPart} />
            </Box>
          </CardContent>
        </Card>

        <Card style={{ width: '100%', marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.partI}
              values={values}
              setValues={setValues}
              handleSubmit={handleSubmit}
            />
          </CardContent>
        </Card>

        <Card style={{ width: '100%', marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.partII}
              values={values}
              setValues={setValues}
              handleSubmit={handleSubmit}
            />
          </CardContent>
        </Card>

        {renderActivityGroup(processField)}

        {/* ============ THÀNH TÍCH CÁ NHÂN ============ */}
        {bigHeader('THÀNH TÍCH CÁ NHÂN')}

        <Card style={{ width: '100%', marginTop: 16 }}>
          <CardContent>
            <Typography variant='body2' style={{ fontWeight: 700, color: SECONDARY_COLOR }}>
              Lưu ý khi khai hồ sơ:
            </Typography>
            <Box component='ul' style={{ marginTop: 4, paddingLeft: 20 }}>
              <Typography component='li' variant='body2' style={{ fontStyle: 'italic', textAlign: 'justify', color: SECONDARY_COLOR }}>
                Các minh chứng scan bản dấu đỏ. Trong trường hợp minh chứng không
                phải là tiếng Anh hoặc tiếng Việt thì cần có văn bản dịch thuật
                minh chứng thành tích đó sang tiếng Anh hoặc tiếng Việt có xác
                nhận của cơ quan, đơn vị có thẩm quyền.
              </Typography>
              <Typography component='li' variant='body2' style={{ fontStyle: 'italic', textAlign: 'justify', marginTop: 4, color: SECONDARY_COLOR }}>
                Các công bố khoa học thống nhất tham chiếu Q theo&nbsp;
                <a href='https://www.scimagojr.com/' target='_blank' rel='noreferrer' style={{ color: SECONDARY_COLOR, textDecoration: 'underline' }}>
                  https://www.scimagojr.com/
                </a>
                &nbsp;và theo năm đăng ký xét giải (VD: công bố từ năm 2020 nhưng đăng ký năm 2026 thì tham chiếu Q theo năm 2026).
              </Typography>
              <Typography component='li' variant='body2' style={{ fontStyle: 'italic', textAlign: 'justify', marginTop: 4, color: SECONDARY_COLOR }}>
                Chỉ kê khai các thành tích của ứng viên khi đã thực hiện xong
                (sáng chế đã được công nhận, bài báo đã đăng, chủ trì đề tài đã
                nghiệm thu… không đánh giá thành tích đang thực hiện).
              </Typography>
              <Typography component='li' variant='body2' style={{ fontStyle: 'italic', textAlign: 'justify', marginTop: 4, color: SECONDARY_COLOR }}>
                Danh mục tạp chí của 6 ngành/liên ngành xét giải áp dụng theo quy định của HĐGSNN năm 2026.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* ============ A. CÔNG TRÌNH TIÊU BIỂU ============ */}
        {sectionHeader('A. CÔNG TRÌNH TIÊU BIỂU')}
        <Card id='summary-all' style={{ width: '100%', marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.typicalWork}
              values={values}
              setValues={setValues}
              handleSubmit={handleSubmit}
            />
          </CardContent>
        </Card>

        {/* ============ B. THÀNH TÍCH KHOA HỌC ============ */}
        {sectionHeader('B. THÀNH TÍCH KHOA HỌC')}
        {achievementFields.map((e) => (
          <Card key={e.index} style={{ width: '100%', marginTop: 32 }}>
            <Box padding='8px 16px' display='flex' alignItems='center'>
              <Typography variant='h6' style={{ flexGrow: 1 }}>
                <b>{e.index}. {e.title}</b>
              </Typography>
            </Box>
            {e.children.map((item) => (
              <Fragment key={item.index}>
                <Divider />
                <Box padding='2px 4px 2px 16px' display='flex' alignItems='center'>
                  <Typography variant='h6' align="justify" style={{ flexGrow: 1, marginRight: 32 }}>
                    <b>{item?.index}. {item?.title}</b>
                  </Typography>
                  <Tooltip arrow title={<ThanhTichAlert closable={false} />}>
                    <Info style={{ cursor: "pointer" }} />
                  </Tooltip>
                  <IconButton color='primary' onClick={() => setDialog({ ...item, actionType: 'insert', data: {} })}><Add /></IconButton>
                </Box>
                <Divider />
                <KPTable nonBorder={true} columns={columnRender(item, setDialog, setPreviewFile)} rows={item.data || []} />
              </Fragment>
            ))}
          </Card>
        ))}

        {/* ============ C. THÀNH TÍCH KHEN THƯỞNG VÀ HOẠT ĐỘNG CỘNG ĐỒNG ============ */}
        {sectionHeader('C. THÀNH TÍCH KHEN THƯỞNG VÀ HOẠT ĐỘNG CỘNG ĐỒNG')}
        {activityFields.map((active) => renderActivityGroup(active))}

        {/* ============ D. CAM KẾT CỦA ỨNG VIÊN ============ */}
        {sectionHeader('D. CAM KẾT CỦA ỨNG VIÊN')}
        <Card style={{ width: '100%', marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.commitment}
              values={commitment}
              setValues={setCommitment}
            />
            <Box marginTop={2}>
              <KPTextform
                component={Box}
                textForm={textForm.camKetFile}
                values={values}
                setValues={setValues}
                handleSubmit={handleSubmit}
              />
            </Box>
          </CardContent>
        </Card>

        {/* ============ E. XÁC NHẬN HỒ SƠ ============ */}
        {sectionHeader('E. XÁC NHẬN HỒ SƠ')}
        <Card style={{ width: '100%', marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.confirmationFile}
              values={values}
              setValues={setValues}
              handleSubmit={handleSubmit}
            />
          </CardContent>
        </Card>

        <Card style={{ width: '100%', marginTop: 16 }}>
          <CardContent>
            <Grid container spacing={2} justifyContent='center'>
              <Grid item xs={12}>
                <Typography variant='subtitle1' style={{ color: SECONDARY_COLOR }}>
                  <i>
                    <b>Lưu ý: </b>Công văn giới thiệu, bảng điểm, bằng khen, giấy khen, giấy chứng nhận hay các văn
                    bản có liên quan làm minh chứng phải được scan bản gốc hoặc có chữ ký, đóng dấu đỏ của đơn vị chủ
                    quản/cơ quan, tổ chức có thẩm quyền.
                  </i>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox color='secondary' checked={acceptRules} onChange={() => setAcceptRules(!acceptRules)} />}
                  label={
                    <Typography variant='subtitle1'>
                      <b style={{ fontFamily: 'SFProDisplay' }}>
                        Tôi xin hoàn toàn chịu trách nhiệm trước pháp luật, Ban tổ chức Giải thưởng về tính chính xác
                        và các thông tin đã khai trong hồ sơ đăng ký trực tuyến tham gia xét giải thưởng
                      </b>
                    </Typography>
                  }
                  style={{ marginTop: 48 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth disabled={!acceptRules} variant='outlined' onClick={() => handleSave('unsubmit')}>
                  Lưu bản nháp
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  disabled={!acceptRules}
                  form='form-update'
                  type='submit'
                  color='secondary'
                  variant='contained'
                >
                  Nộp hồ sơ
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>

      <ThanhTichInsert dialog={dialog} setDialog={setDialog} />


      <Loading open={loading} />
      <PreviewModal open={!!previewFile} url={previewFile} handleClose={() => setPreviewFile(null)} />

      <Dialog fullWidth maxWidth="xs" open={!loading && (isNotAvalaible || warningOpen)}>
        <DialogTitle style={{ padding: "4px 12px 4px 24px" }}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant="h5"><b>Thông báo</b></Typography>
            {!isNotAvalaible && (
              // Chưa đóng cổng thì có thể tắt
              <IconButton onClick={() => setWarningOpen(false)}><Close /></IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" width="100%" marginBottom={4}>
            <WarningRounded style={{ margin: "auto", color: "#4da6ff", fontSize: 72 }} />
          </Box>
          <Typography variant="h6" style={{ textAlign: "center" }}>
            {isNotAvalaible ? (
              <>
                {isNotAvalaible}
                <br />
                <b>Giải thưởng Khuê Văn Các năm {openSeason?.year}</b>
                </>
            ) : (
              <b>{application?.status === "in_review" && "Hồ sơ đã được nộp trên hệ thống!"}</b>
            )}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px" }}>
          {isNotAvalaible ? (
            <Button fullWidth color="secondary" variant="contained" href={ROUTER.HOME}>
              Về trang chủ
            </Button>
          ) : (
            <>
              {application?.status === "in_review" && (
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      href={ROUTER.QCV.HOME}
                      variant="outlined"
                      startIcon={<Home />}
                      onClick={() => setWarningOpen(false)}
                    >
                      Trang chủ
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      startIcon={<Edit />}
                      style={{ float: "right" }}
                      onClick={() => setWarningOpen(false)}
                    >
                      Chỉnh sửa hồ sơ
                    </Button>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
